import { PreflightTestReport } from 'twilio-video';

/**
 * Describes how the user's device connected to the media network during the
 * preflight test, derived purely from the ICE candidate that was ultimately
 * selected. This is "Signal A" of the restrictive-network / VPN heuristic and
 * requires no third-party lookup — it is computed entirely from data already
 * present in the Twilio preflight report.
 *
 * - `direct`       — a host or server-reflexive (STUN) candidate was selected.
 *                    This is the ideal path; traffic flows peer-to-peer / via
 *                    NAT without a relay.
 * - `relayed-udp`  — a TURN relay was required, but over UDP. This commonly
 *                    happens on symmetric NAT / carrier-grade NAT (e.g. some
 *                    cellular networks) and is a mild signal.
 * - `relayed-tcp`  — a TURN relay was required over TCP/TLS, which almost
 *                    always means UDP was blocked outright. This is the
 *                    strongest signal of a VPN, corporate firewall, or strict
 *                    network security policy.
 * - `null`         — the path could not be determined (no report / no selected
 *                    candidate pair).
 */
export type NetworkPathType = 'direct' | 'relayed-udp' | 'relayed-tcp' | null;

/**
 * Derives the {@link NetworkPathType} from a Twilio preflight report.
 *
 * We inspect `selectedIceCandidatePairStats.localCandidate`, which describes
 * the winning local ICE candidate:
 *   - `candidateType` is one of `host` | `srflx` | `prflx` | `relay`.
 *   - When `candidateType === 'relay'`, `relayProtocol` (`udp` | `tcp` | `tls`)
 *     tells us how the client reached the TURN server.
 */
export function getNetworkPathType(report: PreflightTestReport | null): NetworkPathType {
  const localCandidate = report?.selectedIceCandidatePairStats?.localCandidate;

  if (!localCandidate || !localCandidate.candidateType) {
    return null;
  }

  if (localCandidate.candidateType !== 'relay') {
    // host / srflx / prflx are all considered a direct (non-relayed) path.
    return 'direct';
  }

  // A relay was selected — distinguish UDP from TCP/TLS.
  const relayProtocol = localCandidate.relayProtocol?.toLowerCase();
  if (relayProtocol === 'tcp' || relayProtocol === 'tls') {
    return 'relayed-tcp';
  }

  return 'relayed-udp';
}

/** True when the selected path required a TURN relay (either UDP or TCP/TLS). */
export function isRelayedPath(pathType: NetworkPathType): boolean {
  return pathType === 'relayed-udp' || pathType === 'relayed-tcp';
}

export interface NetworkPathDisplayInfo {
  /** Short human-readable label, e.g. "Direct" or "Relayed (TCP)". */
  label: string;
  /** Whether this path should surface a warning to the user. */
  isWarning: boolean;
}

/**
 * Maps a {@link NetworkPathType} to display metadata used by the UI (the
 * QualityModal row and the Connectivity warning banner).
 */
export function getNetworkPathDisplayInfo(pathType: NetworkPathType): NetworkPathDisplayInfo {
  switch (pathType) {
    case 'direct':
      return { label: 'Direct', isWarning: false };
    case 'relayed-udp':
      return { label: 'Relayed (UDP)', isWarning: true };
    case 'relayed-tcp':
      return { label: 'Relayed (TCP)', isWarning: true };
    default:
      return { label: 'Unknown', isWarning: false };
  }
}
